import PropTypes from "prop-types";

import { createContext, useContext, useEffect, useReducer, useState, useCallback } from "react";

import { useHistory } from "react-router-dom";

import apiClient, { setupAxiosInterceptors } from "../../helpers/apiClient";
import { setActiveCompanyTimeZone } from "../../helpers/operationalHours";

import { actionTypes, setSuccessLogin } from "./actions";

const initialState = {
    isAuthenticated: false,
    user: {},
    needsPasswordChange: false,
};

const userReducer = (state, action) => {
    switch (action.type) {
        case actionTypes.SET_SUCCESS_LOGIN: {
            const { user, needsPasswordChange } = action.payload;
            return {
                ...state,
                isAuthenticated: true,
                user: user || {},
                needsPasswordChange: Boolean(needsPasswordChange),
            };
        }
        case actionTypes.SET_SUCCESS_LOGOUT:
            return initialState;
        default:
            return state;
    }
};

const UserContext = createContext();

export const UserProvider = ({ children }) => {
    const [initializing, setInitializing] = useState(true);
    const navigate = useHistory();
    const [state, dispatch] = useReducer(userReducer, initialState);
    const [myCompanyData, setMyCompanyData] = useState(null);

    const fetchCompanyData = useCallback(async () => {
        try {
            const res = await apiClient.get("/api/company/my-company");

            if (res?.data?.success) {
                setMyCompanyData(res.data.data);
                setActiveCompanyTimeZone(res.data.data);
            }
        } catch {
            // not authenticated or company unavailable
        }
    }, []);

    const updateMyCompanyData = useCallback((patch) => {
        setMyCompanyData((prev) => {
            if (!prev) return prev;

            if (typeof patch === "function") {
                return patch(prev);
            }

            const next = { ...prev, ...patch };

            if (patch.preferences) {
                next.preferences = { ...prev.preferences, ...patch.preferences };
            }

            if (patch.supportInfo) {
                next.supportInfo = { ...prev.supportInfo, ...patch.supportInfo };
            }

            return next;
        });
    }, []);

    const restoreSession = useCallback(async () => {
        try {
            const res = await apiClient.get("/api/account/me");
            if (res?.data?.success && res.data.data) {
                dispatch(setSuccessLogin({ user: res.data.data }));
            }
        } catch {
            // no active session
        }
    }, []);

    useEffect(() => {
        setupAxiosInterceptors(dispatch, navigate);
    }, [dispatch, navigate]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            await restoreSession();
            if (!cancelled) {
                setInitializing(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [restoreSession]);

    useEffect(() => {
        if (state.isAuthenticated) {
            fetchCompanyData();
        } else {
            setMyCompanyData(null);
        }
    }, [state.isAuthenticated, fetchCompanyData]);

    if (initializing) return null;

    return (
        <UserContext.Provider
            value={{
                state,
                dispatch,
                myCompanyData,
                fetchCompanyData,
                updateMyCompanyData,
            }}>
            {children}
        </UserContext.Provider>
    );
};

UserProvider.propTypes = {
    children: PropTypes.node,
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error("useUser must be used within a UserProvider");
    }
    return context;
};
